import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import guestService from '../../../services/guestService';

const RSVPManager = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [rsvpData, setRsvpData] = useState({
    totalInvited: 0,
    totalResponded: 0,
    confirmed: 0,
    declined: 0,
    pending: 0,
    totalAttending: 0,
    deadline: '2025-12-31'
  });

  const [recentRSVPs, setRecentRSVPs] = useState([]);
  const [pendingFollowUps, setPendingFollowUps] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadRSVPData();
    }
  }, [user]);

  const loadRSVPData = async () => {
    setLoading(true);

    const [statsResult, recentResult, pendingResult] = await Promise.all([
      guestService.getRSVPStats(user.id),
      guestService.getRecentRSVPs(user.id, 5),
      guestService.getPendingFollowUps(user.id)
    ]);

    if (statsResult.success) {
      setRsvpData(prev => ({ ...prev, ...statsResult.data }));
    }

    if (recentResult.success) {
      setRecentRSVPs(recentResult.data);
    }

    if (pendingResult.success) {
      setPendingFollowUps(pendingResult.data);
    }

    setLoading(false);
  };

  const responseRate = rsvpData.totalInvited > 0
    ? Math.round((rsvpData.totalResponded / rsvpData.totalInvited) * 100)
    : 0;
  const confirmationRate = rsvpData.totalResponded > 0
    ? Math.round((rsvpData.confirmed / rsvpData.totalResponded) * 100)
    : 0;
  const daysUntilDeadline = Math.ceil((new Date(rsvpData.deadline) - new Date()) / (1000 * 60 * 60 * 24));

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return 'bg-green-100 text-green-800';
      case 'Declined': return 'bg-red-100 text-red-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Confirmed': return 'CheckCircle';
      case 'Declined': return 'XCircle';
      case 'Pending': return 'Clock';
      default: return 'HelpCircle';
    }
  };

  const handleSendReminder = async (guestId) => {
    setSending(true);
    const result = await guestService.sendReminder(guestId);

    if (result.success) {
      showToast('Reminder sent successfully!', 'success');
      loadRSVPData(); // Reload to update reminder count
    } else {
      showToast('Failed to send reminder. Please try again.', 'error');
    }
    setSending(false);
  };

  const handleSendBulkReminders = async () => {
    if (pendingFollowUps.length === 0) {
      showToast('No pending follow-ups to send reminders to.', 'info');
      return;
    }

    if (!confirm(`Send reminders to ${pendingFollowUps.length} guests?`)) return;

    setSending(true);
    const guestIds = pendingFollowUps.map(f => f.id);
    const result = await guestService.sendBulkReminders(guestIds);

    if (result.success) {
      showToast(`Sent ${result.data.successful} reminders successfully!`, 'success');
      loadRSVPData();
    } else {
      showToast('Failed to send reminders. Please try again.', 'error');
    }
    setSending(false);
  };

  const handleExportCSV = async () => {
    const result = await guestService.exportCSV(user.id);
    if (result.success) {
      guestService.downloadCSV(result.data, `rsvp-data-${new Date().toISOString().split('T')[0]}.csv`);
      showToast('RSVP data exported successfully', 'success');
    } else {
      showToast('Failed to export data. Please try again.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* RSVP Overview */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-semibold text-2xl text-text-primary">
            RSVP Manager
          </h2>
          <div className="text-right">
            <p className="text-text-secondary text-sm">RSVP Deadline</p>
            <p className="font-semibold text-primary">{new Date(rsvpData.deadline).toLocaleDateString()}</p>
            <p className="text-xs text-text-muted">{daysUntilDeadline} days remaining</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="bg-blue-50 rounded-lg p-4">
              <Icon name="Send" size={24} className="text-blue-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Invited</p>
              <p className="text-2xl font-bold text-blue-600">{rsvpData.totalInvited}</p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-green-50 rounded-lg p-4">
              <Icon name="CheckCircle" size={24} className="text-green-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Confirmed</p>
              <p className="text-2xl font-bold text-green-600">{rsvpData.confirmed}</p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-red-50 rounded-lg p-4">
              <Icon name="XCircle" size={24} className="text-red-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Declined</p>
              <p className="text-2xl font-bold text-red-600">{rsvpData.declined}</p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-yellow-50 rounded-lg p-4">
              <Icon name="Clock" size={24} className="text-yellow-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{rsvpData.pending}</p>
            </div>
          </div>
        </div>

        {/* Progress Bars */}
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-text-primary">Response Rate</span>
              <span className="text-sm text-text-secondary">{responseRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${responseRate}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-text-primary">Confirmation Rate</span>
              <span className="text-sm text-text-secondary">{confirmationRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${confirmationRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent RSVPs */}
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg text-text-primary">
              Recent RSVPs
            </h3>
            <Button variant="outline" size="sm" iconName="Download" onClick={handleExportCSV}>
              Export
            </Button>
          </div>

          <div className="space-y-3">
            {recentRSVPs.length > 0 ? (
              recentRSVPs.map((rsvp) => (
                <div key={rsvp.id} className="flex items-center space-x-3 p-3 bg-surface-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-text-primary">{rsvp.name}</p>
                    <p className="text-sm text-text-secondary">{rsvp.email || 'No email'}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(rsvp.status)}`}>
                      {rsvp.status}
                    </span>
                    <p className="text-xs text-text-muted mt-1">
                      {rsvp.respondedDate ? new Date(rsvp.respondedDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-text-secondary">
                <Icon name="Inbox" size={32} className="mx-auto mb-2 text-text-muted" />
                <p>No recent RSVPs</p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Follow-ups */}
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg text-text-primary">
              Pending Follow-ups
            </h3>
            <Button
              variant="outline"
              size="sm"
              iconName="Send"
              onClick={handleSendBulkReminders}
              disabled={sending || pendingFollowUps.length === 0}
            >
              {sending ? 'Sending...' : 'Send Reminders'}
            </Button>
          </div>

          <div className="space-y-3">
            {pendingFollowUps.length > 0 ? (
              pendingFollowUps.map((followUp) => (
                <div key={followUp.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Icon name="Clock" size={16} className="text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-text-primary">{followUp.name}</p>
                    <p className="text-sm text-text-secondary">{followUp.email || followUp.phone || 'No contact'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-yellow-600">
                      {followUp.daysSinceInvite} days
                    </p>
                    <p className="text-xs text-text-muted">
                      {followUp.reminderCount > 0 ? `${followUp.reminderCount} reminders` : 'since invite'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="MessageCircle"
                    onClick={() => handleSendReminder(followUp.id)}
                    disabled={sending}
                    title="Send reminder"
                  >
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-text-secondary">
                <Icon name="CheckCircle" size={32} className="mx-auto mb-2 text-green-500" />
                <p>All guests have responded!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RSVP Actions */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          RSVP Actions
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 border border-border rounded-lg">
            <Icon name="Send" size={24} className="text-blue-600 mx-auto mb-2" />
            <h4 className="font-medium text-text-primary mb-2">Send Invitations</h4>
            <p className="text-sm text-text-secondary mb-3">
              Go to Guest List to send RSVP invitations
            </p>
            <Button
              variant="outline"
              size="sm"
              iconName="Users"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Manage Guests
            </Button>
          </div>

          <div className="text-center p-4 border border-border rounded-lg">
            <Icon name="MessageCircle" size={24} className="text-yellow-600 mx-auto mb-2" />
            <h4 className="font-medium text-text-primary mb-2">Send Reminders</h4>
            <p className="text-sm text-text-secondary mb-3">
              Follow up with {pendingFollowUps.length} guests who haven't responded
            </p>
            <Button
              variant="outline"
              size="sm"
              iconName="Bell"
              onClick={handleSendBulkReminders}
              disabled={sending || pendingFollowUps.length === 0}
            >
              {sending ? 'Sending...' : 'Send Reminders'}
            </Button>
          </div>

          <div className="text-center p-4 border border-border rounded-lg">
            <Icon name="Download" size={24} className="text-green-600 mx-auto mb-2" />
            <h4 className="font-medium text-text-primary mb-2">Export Data</h4>
            <p className="text-sm text-text-secondary mb-3">
              Download complete RSVP data as CSV
            </p>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RSVPManager;